import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, Search, LogOut, Lock, Pencil, Users, Building2, CalendarDays, Star, CheckCircle2, XCircle } from "lucide-react";
import Seo from "../components/Seo";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ROLES, MEMBERSHIP_PREFS, INTERESTS, COUNTRIES } from "../data/content";
import { MEMBER_TYPES } from "../data/hub";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADER = "X-Admin-Passcode";
const KEY = "hub_admin_passcode";
const ALL = "__all__";

const adminGet = (path, pass, opts = {}) =>
  axios.get(`${API}${path}`, { headers: { [HEADER]: pass }, ...opts });

export default function Admin() {
  const [pass, setPass] = useState(sessionStorage.getItem(KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [passInput, setPassInput] = useState("");
  const [view, setView] = useState("registrations");

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", country: "", role: "", membership: "", interest: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [draftStatus, setDraftStatus] = useState("New");
  const [draftNotes, setDraftNotes] = useState("");

  const load = useCallback(async (pc) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [s, r] = await Promise.all([
        adminGet("/admin/summary", pc),
        adminGet("/admin/registrations", pc, { params }),
      ]);
      setSummary(s.data);
      setItems(r.data.items);
    } catch (e) {
      if (e.response?.status === 401) { setAuthed(false); sessionStorage.removeItem(KEY); toast.error("Wrong passcode."); }
      else if (e.response?.status === 503) toast.error("Admin access is disabled on the server.");
      else toast.error("Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    (async () => {
      const saved = sessionStorage.getItem(KEY);
      if (saved) {
        try { await adminGet("/admin/verify", saved); setPass(saved); setAuthed(true); }
        catch { sessionStorage.removeItem(KEY); }
      }
      setChecking(false);
    })();
  }, []);

  useEffect(() => { if (authed && pass) load(pass); }, [authed, pass, load]);

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await adminGet("/admin/verify", passInput);
      sessionStorage.setItem(KEY, passInput);
      setPass(passInput);
      setAuthed(true);
    } catch (err) {
      toast.error(err.response?.status === 503 ? "Admin access is disabled." : "Wrong passcode.");
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await adminGet("/admin/registrations/export.csv", pass, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "stupeco-hub-registrations.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const openEdit = (row) => { setEditing(row); setDraftStatus(row.status || "New"); setDraftNotes(row.internal_notes || ""); };
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/admin/registrations/${editing.id}`, { status: draftStatus, internal_notes: draftNotes }, { headers: { [HEADER]: pass } });
      toast.success("Saved.");
      setEditing(null);
      load(pass);
    } catch { toast.error("Save failed."); }
  };

  const logout = () => { sessionStorage.removeItem(KEY); setAuthed(false); setPass(""); setPassInput(""); };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-[color:var(--paper)]"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>;
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[color:var(--paper)] px-5" data-testid="admin-gate">
        <Seo title="Admin" description="StUpEco Hub admin." path="/admin" />
        <form onSubmit={unlock} className="hub-card w-full max-w-sm rounded-3xl p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[color:var(--soft)] text-[color:var(--ink)]"><Lock className="h-5 w-5" /></span>
          <h1 className="font-display mt-6 text-2xl font-semibold text-[color:var(--ink)]">Registrations admin</h1>
          <p className="mt-2 text-sm text-[color:var(--ink-soft)]">Enter the internal passcode to continue.</p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="pc">Passcode</Label>
            <Input id="pc" type="password" data-testid="admin-passcode" value={passInput} onChange={(e) => setPassInput(e.target.value)} placeholder="••••••••" className="h-11 rounded-xl" autoComplete="off" />
          </div>
          <button type="submit" data-testid="admin-unlock" className="mt-6 w-full rounded-full bg-[color:var(--invert-bg)] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Seo title="Admin" description="StUpEco Hub admin." path="/admin" />
      <header className="border-b border-[color:var(--hairline)] bg-[color:var(--surface-glass)] backdrop-blur-xl">
        <div className="container-hub flex h-16 items-center justify-between">
          <div className="flex items-center gap-3"><Logo showHub={false} /><span className="font-display text-sm font-semibold text-[color:var(--ink)]">Admin</span></div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]"><LogOut className="h-4 w-4" /> Lock</button>
          </div>
        </div>
      </header>

      <main className="container-hub py-10" data-testid="admin-dashboard">
        <div className="mb-8 inline-flex rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] p-1">
          <button onClick={() => setView("registrations")} data-testid="admin-tab-registrations"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === "registrations" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)]"}`}>
            <Users className="h-4 w-4" /> Early-access
          </button>
          <button onClick={() => setView("members")} data-testid="admin-tab-members"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === "members" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)]"}`}>
            <Building2 className="h-4 w-4" /> Members
          </button>
          <button onClick={() => setView("events")} data-testid="admin-tab-events"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === "events" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)]"}`}>
            <CalendarDays className="h-4 w-4" /> Events
          </button>
        </div>

        {view === "members" ? (
          <MembersPanel pass={pass} />
        ) : view === "events" ? (
          <EventsPanel pass={pass} />
        ) : (
        <>
        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="hub-card rounded-2xl p-5">
            <div className="flex items-center gap-2 text-[color:var(--ink-soft)]"><Users className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Total</span></div>
            <p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]" data-testid="admin-total">{summary?.total ?? 0}</p>
          </div>
          {summary && Object.entries(summary.by_status).map(([s, c]) => (
            <div key={s} className="hub-card rounded-2xl p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">{s}</p>
              <p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]">{c}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
            <Input data-testid="admin-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, email or business" className="h-11 rounded-xl pl-9" />
          </div>
          <FilterSelect label="Country" value={filters.country} onChange={(v) => setFilters((f) => ({ ...f, country: v }))} options={summary?.countries || []} testId="admin-filter-country" />
          <FilterSelect label="Role" value={filters.role} onChange={(v) => setFilters((f) => ({ ...f, role: v }))} options={ROLES} testId="admin-filter-role" />
          <FilterSelect label="Membership" value={filters.membership} onChange={(v) => setFilters((f) => ({ ...f, membership: v }))} options={MEMBERSHIP_PREFS} testId="admin-filter-membership" />
          <FilterSelect label="Interest" value={filters.interest} onChange={(v) => setFilters((f) => ({ ...f, interest: v }))} options={INTERESTS} testId="admin-filter-interest" />
          <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={summary?.statuses || []} testId="admin-filter-status" />
          <button onClick={exportCsv} data-testid="admin-export" className="inline-flex h-11 items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)]">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-table">
              <thead className="border-b border-[color:var(--hairline)] bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
                <tr>
                  {["Name", "Email", "Country", "Role", "Membership", "Interests", "Status", "Registered", ""].map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-[color:var(--ink-soft)]"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-[color:var(--ink-soft)]">No registrations match your filters.</td></tr>
                ) : items.map((r) => (
                  <tr key={r.id} className="border-b border-[color:var(--hairline)] last:border-0 hover:bg-[color:var(--soft)]">
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-[color:var(--ink)]">{r.name}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.email}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.country}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.role}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.preferred_membership}</td>
                    <td className="px-4 py-3 text-[color:var(--ink-soft)]">{(r.interests || []).length}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className="rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-xs font-semibold text-[color:var(--ink)]">{r.status || "New"}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3"><button onClick={() => openEdit(r)} data-testid={`admin-edit-${r.id}`} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]"><Pencil className="h-3 w-3" /> Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle>Edit registration</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="rounded-xl bg-[color:var(--soft)] p-4 text-sm">
                <p className="font-medium text-[color:var(--ink)]">{editing.name}</p>
                <p className="text-[color:var(--ink-soft)]">{editing.email} · {editing.country}</p>
                {(editing.interests || []).length > 0 && <p className="mt-2 text-xs text-[color:var(--ink-soft)]">Interests: {editing.interests.join(", ")}</p>}
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={draftStatus} onValueChange={setDraftStatus}>
                  <SelectTrigger data-testid="admin-edit-status" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(summary?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Internal notes</Label>
                <Textarea data-testid="admin-edit-notes" value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} placeholder="Private notes about this registrant…" className="min-h-28 rounded-xl" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)]">Cancel</button>
            <button onClick={saveEdit} data-testid="admin-edit-save" className="rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger data-testid={testId} className="h-11 w-[150px] rounded-xl">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={ALL}>{`All ${label.toLowerCase()}`}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function MembersPanel({ pass }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ member_types: [], statuses: [], tiers: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", member_type: "", country: "", membership: "", onboarding: "", discoverable: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [draftTier, setDraftTier] = useState("free");
  const [draftStatus, setDraftStatus] = useState("Active");
  const [draftNotes, setDraftNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axios.get(`${API}/admin/members`, { headers: { [HEADER]: pass }, params });
      setItems(data.items);
      setMeta({ member_types: data.member_types, statuses: data.statuses, tiers: data.tiers });
    } catch { toast.error("Failed to load members."); }
    finally { setLoading(false); }
  }, [filters, pass]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => { setEditing(row); setDraftTier(row.membership_tier || "free"); setDraftStatus(row.status || "Active"); setDraftNotes(row.internal_notes || ""); };
  const save = async () => {
    try {
      await axios.patch(`${API}/admin/members/${editing.user_id}`, { membership_tier: draftTier, status: draftStatus, internal_notes: draftNotes }, { headers: { [HEADER]: pass } });
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch { toast.error("Save failed."); }
  };

  const distinctCountries = [...new Set(items.map((i) => i.country).filter(Boolean))].sort();

  return (
    <div data-testid="admin-members-panel">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="hub-card rounded-2xl p-5"><p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Total members</p><p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]" data-testid="admin-members-total">{items.length}</p></div>
        <div className="hub-card rounded-2xl p-5"><p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Onboarded</p><p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]">{items.filter((i) => i.onboarding_completed).length}</p></div>
        <div className="hub-card rounded-2xl p-5"><p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Premium</p><p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]">{items.filter((i) => i.membership_tier === "premium").length}</p></div>
        <div className="hub-card rounded-2xl p-5"><p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Discoverable</p><p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]">{items.filter((i) => i.discoverable_preference === "yes").length}</p></div>
      </div>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, email or business" className="h-11 rounded-xl pl-9" data-testid="admin-members-search" />
        </div>
        <FilterSelect label="Member type" value={filters.member_type} onChange={(v) => setFilters((f) => ({ ...f, member_type: v }))} options={MEMBER_TYPES} testId="admin-members-filter-type" />
        <FilterSelect label="Country" value={filters.country} onChange={(v) => setFilters((f) => ({ ...f, country: v }))} options={distinctCountries.length ? distinctCountries : COUNTRIES} testId="admin-members-filter-country" />
        <FilterSelect label="Tier" value={filters.membership} onChange={(v) => setFilters((f) => ({ ...f, membership: v }))} options={meta.tiers || ["free","standard","premium"]} testId="admin-members-filter-tier" />
        <FilterSelect label="Onboarding" value={filters.onboarding} onChange={(v) => setFilters((f) => ({ ...f, onboarding: v }))} options={["complete","incomplete"]} testId="admin-members-filter-onboarding" />
        <FilterSelect label="Discoverable" value={filters.discoverable} onChange={(v) => setFilters((f) => ({ ...f, discoverable: v }))} options={["yes","not_yet"]} testId="admin-members-filter-discover" />
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={meta.statuses || ["Active","Pending","Suspended","Archived"]} testId="admin-members-filter-status" />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="admin-members-table">
            <thead className="border-b border-[color:var(--hairline)] bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
              <tr>{["Name","Email","Type","Country","Business","Tier","% Complete","Onboarded","Discover","Status","Joined",""].map((h) => (<th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide">{h}</th>))}</tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan={12} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[color:var(--ink-soft)]" /></td></tr>)
              : items.length === 0 ? (<tr><td colSpan={12} className="px-4 py-10 text-center text-[color:var(--ink-soft)]">No members match your filters.</td></tr>)
              : items.map((r) => (
                <tr key={r.user_id} className="border-b border-[color:var(--hairline)] last:border-0 hover:bg-[color:var(--soft)]">
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-[color:var(--ink)]">{r.name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.email}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.member_type || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.country || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.business_name || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)] capitalize">{r.membership_tier}</td>
                  <td className="px-4 py-3 text-[color:var(--ink-soft)]">{r.profile_completion}%</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.onboarding_completed ? <span className="text-[color:var(--brand-green)]">Yes</span> : <span className="text-[color:var(--ink-soft)]">No</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{r.discoverable_preference}</td>
                  <td className="whitespace-nowrap px-4 py-3"><span className="rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-xs font-semibold text-[color:var(--ink)]">{r.status}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><button onClick={() => openEdit(r)} data-testid={`admin-member-edit-${r.user_id}`} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]"><Pencil className="h-3 w-3" /> Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-member-dialog">
          <DialogHeader><DialogTitle>Edit member</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="rounded-xl bg-[color:var(--soft)] p-4 text-sm"><p className="font-medium text-[color:var(--ink)]">{editing.name}</p><p className="text-[color:var(--ink-soft)]">{editing.email}</p></div>
              <div className="space-y-2"><Label>Membership tier</Label>
                <Select value={draftTier} onValueChange={setDraftTier}><SelectTrigger data-testid="admin-member-tier" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.tiers || ["free","standard","premium"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={draftStatus} onValueChange={setDraftStatus}><SelectTrigger data-testid="admin-member-status" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.statuses || ["Active","Pending","Suspended","Archived"]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Internal notes</Label>
                <Textarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} className="min-h-28 rounded-xl" data-testid="admin-member-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)]">Cancel</button>
            <button onClick={save} data-testid="admin-member-save" className="rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventsPanel({ pass }) {
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState(["draft", "pending", "approved", "rejected", "archived"]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", status: "pending" });
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      const { data } = await axios.get(`${API}/admin/events`, { headers: { [HEADER]: pass }, params });
      setItems(data.items);
      if (data.statuses) setStatuses(data.statuses);
    } catch { toast.error("Failed to load events."); }
    finally { setLoading(false); }
  }, [filters, pass]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, label) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/events/${id}`, body, { headers: { [HEADER]: pass } });
      toast.success(label);
      load();
    } catch { toast.error("Update failed."); }
    finally { setBusyId(""); }
  };

  const counts = statuses.reduce((acc, s) => ({ ...acc, [s]: items.filter((i) => i.status === s).length }), {});

  return (
    <div data-testid="admin-events-panel">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <div className="hub-card rounded-2xl p-5"><p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Total shown</p><p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]" data-testid="admin-events-total">{items.length}</p></div>
        {statuses.map((s) => (
          <div key={s} className="hub-card rounded-2xl p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)] capitalize">{s}</p>
            <p className="font-display mt-2 text-3xl font-semibold text-[color:var(--ink)]">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search title, organizer or description" className="h-11 rounded-xl pl-9" data-testid="admin-events-search" />
        </div>
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={statuses} testId="admin-events-filter-status" />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="admin-events-table">
            <thead className="border-b border-[color:var(--hairline)] bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
              <tr>{["Title", "Type", "Format", "When", "Location", "Organizer", "Status", "Featured", "Actions"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan={9} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[color:var(--ink-soft)]" /></td></tr>)
              : items.length === 0 ? (<tr><td colSpan={9} className="px-4 py-10 text-center text-[color:var(--ink-soft)]">No events match your filters.</td></tr>)
              : items.map((e) => (
                <tr key={e.id} className="border-b border-[color:var(--hairline)] last:border-0 hover:bg-[color:var(--soft)]">
                  <td className="px-4 py-3 font-medium text-[color:var(--ink)]"><div className="line-clamp-2 max-w-[260px]">{e.title}</div><div className="mt-0.5 text-[10px] uppercase tracking-wide text-[color:var(--ink-soft)]">{e.is_demo ? "Demo" : ""}</div></td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{e.event_type}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{e.format}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{(e.start_datetime || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{[e.city, e.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--ink-soft)]">{e.organizer_name || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3"><span className="rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-xs font-semibold capitalize text-[color:var(--ink)]">{e.status}</span></td>
                  <td className="whitespace-nowrap px-4 py-3">{e.featured ? <span className="inline-flex items-center gap-1 text-[color:var(--brand-orange)]"><Star className="h-3 w-3 fill-[color:var(--brand-orange)]" /> Yes</span> : <span className="text-[color:var(--ink-soft)]">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <a href={`/app/events/${e.id}`} target="_blank" rel="noreferrer" className="rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-xs font-semibold text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]" data-testid={`admin-event-view-${e.id}`}>View</a>
                      {e.status !== "approved" && (
                        <button disabled={busyId === e.id} onClick={() => patch(e.id, { status: "approved" }, "Approved.")} data-testid={`admin-event-approve-${e.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-green)]/10 px-3 py-1.5 text-xs font-semibold text-[color:var(--brand-green)] hover:bg-[color:var(--brand-green)]/20 disabled:opacity-60">
                          <CheckCircle2 className="h-3 w-3" /> Approve
                        </button>
                      )}
                      {e.status !== "rejected" && (
                        <button disabled={busyId === e.id} onClick={() => patch(e.id, { status: "rejected" }, "Rejected.")} data-testid={`admin-event-reject-${e.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-red)]/10 px-3 py-1.5 text-xs font-semibold text-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/20 disabled:opacity-60">
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      )}
                      <button disabled={busyId === e.id} onClick={() => patch(e.id, { featured: !e.featured }, e.featured ? "Unfeatured." : "Featured.")} data-testid={`admin-event-feature-${e.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)] disabled:opacity-60">
                        <Star className={`h-3 w-3 ${e.featured ? "fill-[color:var(--brand-orange)] text-[color:var(--brand-orange)]" : ""}`} />
                        {e.featured ? "Unfeature" : "Feature"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

